import styled from '@emotion/styled';
import { css } from '@emotion/react';

// Theme constants
const COLORS = {
  primary: '#2A70F0',
  primaryHover: '#1A60E0',
  black: '#000000',
  lightBorder: '#00000014',
  white: '#FFFFFF',
  hoverGray: '#F0F0F0',
};

const SPACING = {
  row: '16px 0',
  buttonGap: '10px',
  containerTop: '32px',
};

const SIZES = {
  buttonHeight: '48px',
  borderRadius: '12px',
  fontSize: {
    normal: '24px',
  },
};

// Common style blocks that can be reused
const flexCenter = css`
  display: flex;
  align-items: center;
`;

const fullWidth = css`
  width: 100%;
`;

const boldText = css`
  font-weight: 700;
  font-size: ${SIZES.fontSize.normal};
  line-height: 32px;
`;

// Base textarea styles
const baseTextareaStyles = css`
  ${fullWidth}
  ${boldText}
  flex: 1;
  border-radius: ${SIZES.borderRadius};
  resize: none;
  font-family: inherit;
  overflow-y: hidden;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${COLORS.black};
  }
`;

// Row components
export const StyledBlogEditorRow = styled.div`
  ${flexCenter}
  justify-content: space-between;
  ${fullWidth}
  padding: ${SPACING.row};
`;

export const StyledBlogEditorRowDisplayBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${fullWidth}
  padding: ${SPACING.row};
`;

// Text components
export const Title = styled.label`
  ${boldText}
  color: ${COLORS.black};
  padding-right: 30px;
`;

export const StyledDivider = styled.div`
  padding: ${SPACING.row};
  ${boldText}
  color: ${COLORS.black};
`;

// Textarea components
export const TitleTextarea = styled.textarea<{ hasValue?: boolean }>`
  ${baseTextareaStyles}
  padding: 10px 26px;
  border: 1px solid ${(props) => (props.hasValue ? COLORS.black : COLORS.lightBorder)};
`;

export const ContentTextarea = styled.textarea<{ hasValue?: boolean }>`
  ${baseTextareaStyles}
  padding: 16px 26px;
  border: 1px solid ${(props) => (props.hasValue ? COLORS.black : COLORS.lightBorder)};
`;

export const PageImageTextarea = styled.textarea<{ hasValue?: boolean }>`
  ${baseTextareaStyles}
  padding: 10px 26px;
  border: 1px solid ${(props) => (props.hasValue ? COLORS.black : COLORS.lightBorder)};
`;

// Button components
export const RemoveButton = styled.button`
  ${flexCenter}
  justify-content: center;
  margin-left: 30px;
  width: 36px;
  height: 36px;
  border: 1px solid ${COLORS.lightBorder};
  background: none;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background-color: ${COLORS.hoverGray};
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  ${fullWidth}
  margin-top: ${SPACING.containerTop};
`;

// Button variant types
type ButtonVariant = 'save' | 'add';

export const BlogEditorButton = styled.button<{
  variant?: ButtonVariant;
  fullWidth?: boolean;
}>`
  ${flexCenter}
  justify-content: center;
  padding: 7px 12px;
  gap: ${SPACING.buttonGap};
  width: ${(props) => (props.fullWidth ? '100%' : 'auto')};
  height: ${SIZES.buttonHeight};
  font-size: ${SIZES.fontSize.normal};
  font-weight: 700;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.2s;

  ${(props) => {
    const variant = props.variant || 'add';

    if (variant === 'save') {
      return css`
        background-color: ${COLORS.primary};
        color: ${COLORS.white};
        border: 1px solid ${COLORS.primary};

        &:hover {
          background-color: ${COLORS.primaryHover};
        }
      `;
    }

    return css`
      background-color: ${COLORS.white};
      color: ${COLORS.primary};
      border: 1px solid ${COLORS.lightBorder};

      &:hover {
        background-color: ${COLORS.hoverGray};
      }
    `;
  }}
`;
